import { useMemo, useRef, useState } from "react";
import { trpc } from "../lib/trpc";
import { parseTrNumber } from "../lib/trNumber";
import { fold } from "../lib/trSearch";
import { Icon } from "../components/ui/Icon";
import { PageFormShell } from "../components/ui/PageFormShell";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { useBulkPeriod, useCloseBulkTab, fmtH, MAX_ROWS } from "../components/hr/bulkShared";
import "./Items.css";        // .items-page (PageFormShell) · .dt2 .cell-num · .iact
import "./Maintenance.css";  // .mt-empty (liste boş / yükleniyor durumu)
import "../components/hr/BulkEntry.css";

/**
 * TOPLU FAZLA MESAİ GİRİŞİ — uygulama içi SEKME (eskiden çekmeceydi).
 *
 * ── Neden sekme ──
 * Çekmecede iken kullanıcı 60 kişilik listeyi doldururken vardiya planına ya da puantaja
 * bakamıyordu: sayfadan çıkmak çekmeceyi ve yarım kalan girişi siliyordu. Sekme olarak
 * ekran mount kalır; başka sekmeye bakıp geri dönüldüğünde seçimler ve satırlar yerinde
 * durur (TabViews keep-alive).
 *
 * ── Dönem sayfanın kendisinde ──
 * Ay seçici artık burada. Puantaj ekranından "Mesai" ile gelince o ekranın ayı `?d=YYYY-MM`
 * ile taşınır; sayfa kendi başına (derin bağlantı, sekme geri yükleme) açıldığında içinde
 * bulunulan aya düşer.
 *
 * ── PARA GÖRÜNÜRLÜĞÜ ──
 * Saati `manage:hr` olan herkes girer; TL sütunları YALNIZ süper-admine gösterilir ve ayrı
 * bir uçtan (`attendance.overtimePayMonth`, assertSalaryAccess) gelir. Gerekçe: oranı
 * tanımlı olmayan personelde ücret brütten türetilir (brüt ÷ bölen × katsayı), yani tutarı
 * gören kişi bölmeyi tersine çevirip MAAŞI hesaplayabilir.
 *
 * ── ÜZERİNE YAZAR, TOPLAMAZ ──
 * Sunucu aynı personel-gün için saati üzerine yazar. Satırda o günün MEVCUT saati
 * gösterilir, böylece değişiklik sessiz olmaz.
 */

export const BULK_OVERTIME_PATH = "/personel/mesai";

interface RowState {
  /** "YYYY-MM-DD" */
  date: string;
  /** Serbest metin — TR ondalık kabul eder ("1,5"). */
  hours: string;
}

const fmtTL = (n: number, cur = "TRY"): string =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + " " + cur;
/**
 * SAAT ÜCRETİ 4 ondalığa kadar basılır (tutarlar 2'de kalır).
 * Oran maaştan türetildiğinde küsuratlı olur (52.000 ÷ 225 × 1,5 = 346,6667). İki ondalığa
 * kırpılsaydı kullanıcı elle çarptığında bir kuruş tutmaz ve rakama güveni sarsılırdı.
 */
const fmtRateTL = (n: number, cur = "TRY"): string =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n) + " " + cur;

const RATE_SOURCE_LABEL: Record<string, string> = { position: "pozisyon", employee: "kişiye özel" };

export function BulkOvertimePage() {
  const qc = trpc.useUtils();
  const back = useCloseBulkTab(BULK_OVERTIME_PATH);
  const per = useBulkPeriod();
  const { year, month } = per;

  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuper = !!meQ.data?.user?.isSuperAdmin;
  const canManage = isSuper || (meQ.data?.user?.permissions ?? []).includes("manage:hr");

  const monthQ = trpc.hr.attendance.overtimeMonth.useQuery({ year, month }, { enabled: per.valid });
  // Para AYRI uçtan ve yalnız süper-admin için — `manage:hr` kullanıcısına tek bayt tutar gitmez.
  const payQ = trpc.hr.attendance.overtimePayMonth.useQuery({ year, month }, { enabled: per.valid && isSuper });
  // Dönem kilidi: bordrosu onaylanmış/ödenmiş ayın puantajı değiştirilemez (sunucu da reddeder).
  const statusQ = trpc.hr.attendance.monthStatus.useQuery({ year, month }, { enabled: per.valid, staleTime: 5_000 });
  const locked = statusQ.isLoading || statusQ.data?.payrollStatus === "approved" || statusQ.data?.payrollStatus === "paid";

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) =>
    setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const [q, setQ] = useState("");
  /** employeeId → { date, hours }. Map çünkü her seçili personelin AYRI değerleri var. */
  const [picked, setPicked] = useState<Map<string, RowState>>(new Map());
  const [applyDate, setApplyDate] = useState("");
  const [applyHours, setApplyHours] = useState("");

  const people = monthQ.data?.employees ?? [];
  const pay = payQ.data;
  const payByEmp = useMemo(() => {
    const m = new Map<string, { hourlyRate: number; multiplier: number; overtimePay: number; rateSource: string | null; currency: string }>();
    for (const p of payQ.data?.employees ?? []) m.set(p.employeeId, p);
    return m;
  }, [payQ.data]);

  /**
   * Saat ücreti KAPSAMDAKİ HER personel için gelir (mesaisi olmayanın saati 0, tutarı 0'dır) —
   * kullanıcı saati YAZARKEN ücreti görebilsin diye. Rakam sunucudaki `overtimePayFor`
   * fonksiyonundan çıkar; burada TAHMİN yapılmaz, yalnız saatle çarpılır.
   */
  const rateOf = (employeeId: string) => payByEmp.get(employeeId) ?? null;

  const filtered = useMemo(() => {
    const qq = fold(q.trim());
    if (!qq) return people;
    return people.filter((p) => fold(p.name).includes(qq) || fold(p.code ?? "").includes(qq) || fold(p.positionTitle ?? "").includes(qq));
  }, [people, q]);

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, { date: applyDate, hours: applyHours });
      return next;
    });
  };
  const selectAllShown = () => {
    setPicked((prev) => {
      const next = new Map(prev);
      // Sıra GÖRÜNEN listeden alınır; Map ekleme sırası değil (ShiftPlanTab'da bug'dı).
      for (const p of filtered) if (!next.has(p.employeeId)) next.set(p.employeeId, { date: applyDate, hours: applyHours });
      return next;
    });
  };
  const setRow = (id: string, patch: Partial<RowState>) => {
    setPicked((prev) => {
      const next = new Map(prev);
      const cur = next.get(id);
      if (cur) next.set(id, { ...cur, ...patch });
      return next;
    });
  };
  /** "Hepsine uygula" — en sık durum: aynı gün birden çok kişi mesai yaptı. */
  const spread = () => {
    setPicked((prev) => {
      const next = new Map<string, RowState>();
      for (const [id, r] of prev) {
        next.set(id, { date: applyDate || r.date, hours: applyHours || r.hours });
      }
      return next;
    });
  };

  const nameOf = useMemo(() => new Map(people.map((p) => [p.employeeId, p])), [people]);
  const rows = useMemo(() => [...picked.entries()].map(([id, r]) => ({ id, ...r })), [picked]);

  const ymKey = `${year}-${String(month).padStart(2, "0")}`;
  const parsed = rows.map((r) => {
    const h = r.hours.trim() === "" ? NaN : parseTrNumber(r.hours);
    // Alan bazlı geçerlilik: tarih boşken SAAT alanını da kırmızıya boyamak, doğru girilmiş
    // bir değeri hatalı gösterip kullanıcıyı yanlış yere baktırıyordu.
    const dateOk = !!r.date && r.date.slice(0, 7) === ymKey;
    const hoursOk = Number.isFinite(h) && h >= 0 && h <= 16;
    return { ...r, h, dateOk, hoursOk, valid: dateOk && hoursOk };
  });
  const invalidCount = parsed.filter((p) => !p.valid).length;
  const overLimit = parsed.length > MAX_ROWS;
  const canSave = canManage && !locked && per.valid && parsed.length > 0 && invalidCount === 0 && !overLimit;

  const batchHours = parsed.reduce((s, p) => s + (p.valid ? p.h : 0), 0);
  /**
   * PARTİ TUTARI — üç koşul sağlanmazsa GÖSTERİLMEZ (0 değil, "yok"):
   *  (a) politikada mesai ödemesi kapalıysa sunucu 0 yazar, ekranın rakam basması yalan olur;
   *  (b) seçilenlerde birden çok para birimi varsa TRY + USD toplanamaz;
   *  (c) satır tutarları ROUND_HALF_UP ile 2 ondalığa kesilip TOPLANIR — sunucunun
   *      kuruş yuvarlamasıyla aynı sırayı izler.
   */
  const rowPay = (id: string, h: number): number | null => {
    const rt = rateOf(id);
    if (!rt || !pay?.payOvertime) return null;
    return Math.round(rt.hourlyRate * rt.multiplier * h * 100) / 100;
  };
  /**
   * O personelin AYLIK TOPLAM MESAİ HAKEDİŞİ — bu satır KAYDEDİLDİKTEN SONRAKİ hâliyle.
   *
   * Kayıtlı ay toplamından o GÜNÜN mevcut saati düşülür, girilen saat eklenir: uç aynı
   * personel-gün için saati TOPLAMAZ, ÜZERİNE yazar — hakediş de o kuralı izlemeli.
   * Aksi hâlde zaten 3 saati olan bir güne 2 saat yazan kullanıcı toplamı 5 sanırdı.
   */
  const monthAfterRow = (id: string, dateKey: string, newHours: number) => {
    const p = nameOf.get(id);
    const rt = rateOf(id);
    const saved = p?.overtimeHours ?? 0;
    const sameDay = (p?.days ?? []).find((x) => String(x.date).slice(0, 10) === dateKey)?.hours ?? 0;
    const hours = Math.round((saved - sameDay + newHours) * 100) / 100;
    const amount = rt && pay?.payOvertime && rt.hourlyRate > 0
      ? Math.round(rt.hourlyRate * rt.multiplier * hours * 100) / 100
      : null;
    return { hours, amount, currency: rt?.currency ?? "TRY" };
  };

  const batchCurrencies = new Set(parsed.filter((p) => p.valid).map((p) => rateOf(p.id)?.currency).filter(Boolean) as string[]);
  const batchMixed = batchCurrencies.size > 1;
  const batchPay = batchMixed || !pay?.payOvertime
    ? null
    : parsed.reduce((s, p) => s + (p.valid ? (rowPay(p.id, p.h) ?? 0) : 0), 0);

  const bulkMut = trpc.hr.attendance.overtimeBulk.useMutation({
    onSuccess: (res) => {
      qc.hr.attendance.invalidate();
      setPicked(new Map());
      pushToast({
        tone: res.warnings.length ? "warning" : "success",
        title: `${res.total} satır işlendi`,
        message: `${res.created} yeni · ${res.updated} güncellendi${res.cleared ? ` · ${res.cleared} kaldırıldı` : ""}${res.unchanged ? ` · ${res.unchanged} değişmedi` : ""}`,
        lines: res.warnings,
        duration: res.warnings.length ? 0 : undefined,
      });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message, duration: 0 }),
  });

  const save = () => {
    if (!canSave) return;
    bulkMut.mutate({ year, month, entries: parsed.map((p) => ({ employeeId: p.id, date: p.date, hours: p.h })) });
  };

  return (
    <>
      <PageFormShell
        open
        wide
        onClose={back}
        title={`Toplu Fazla Mesai — ${per.label}`}
        subtitle="Personelleri seçin, her satıra tarih ve mesai saatini yazın"
        footer={
          <>
            <span className="bulk-foot-sum" aria-live="polite">
              <b className="mono">{parsed.length}</b> satır · <b className="mono">{fmtH(batchHours)}</b> sa
              {isSuper && batchPay !== null && batchPay > 0
                ? <> · <b className="mono">{fmtTL(batchPay, [...batchCurrencies][0] ?? "TRY")}</b></>
                : null}
              {isSuper && batchMixed ? <span className="warn"> · birden çok para birimi, toplanmadı</span> : null}
              {invalidCount > 0 ? <span className="bad"> · {invalidCount} satır eksik</span> : null}
              {overLimit ? <span className="bad"> · en fazla {MAX_ROWS} satır</span> : null}
            </span>
            <button type="button" className="btn-md ghost" onClick={back}>Vazgeç</button>
            <button type="button" className="btn-md primary" disabled={!canSave || bulkMut.isPending} onClick={save}>
              <Icon name="check" size={14} />{bulkMut.isPending ? "Kaydediliyor…" : "Mesaileri Kaydet"}
            </button>
          </>
        }
      >
        {/* ── Dönem ── */}
        <div className="bulk-period">
          <div className="form-field">
            <label className="lbl" htmlFor="bo-ym">Dönem (ay)</label>
            <input id="bo-ym" className="form-input" type="month" value={per.ym} onChange={(e) => per.setYm(e.target.value)} />
          </div>
          <span className="grow" />
        </div>
        {!per.valid ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="alertTriangle" size={14} />Geçerli bir ay seçin.
          </div>
        ) : null}
        {!canManage && !meQ.isLoading ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="alertTriangle" size={14} />Bu ekranda değişiklik yapma yetkiniz yok (manage:hr) — yalnız görüntüleyebilirsiniz.
          </div>
        ) : null}
        {locked && !statusQ.isLoading ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="lock" size={14} />
            Bu dönemin bordrosu <b>{statusQ.data?.payrollStatus === "paid" ? "ödenmiş" : "onaylanmış"}</b> — puantaj kilitli, mesai girilemez.
          </div>
        ) : null}

        {/* ── Ayın mevcut durumu ── */}
        <div className="bulk-band">
          <div className="bulk-band-i">
            <span className="m-ovl">Bu ay mesai girilen</span>
            <b className="mono">{monthQ.data ? `${monthQ.data.withOvertime} kişi` : "…"}</b>
          </div>
          <div className="bulk-band-i">
            <span className="m-ovl">Toplam mesai</span>
            <b className="mono">{monthQ.data ? `${fmtH(monthQ.data.totalHours)} sa` : "…"}</b>
          </div>
          {isSuper ? (
            <div className="bulk-band-i">
              <span className="m-ovl">Bu ayki mesai ödemesi</span>
              <b className="mono accent">
                {!pay ? "…" : pay.mixedCurrency ? "birden çok para birimi" : !pay.payOvertime ? "kapalı" : fmtTL(pay.totalPay, pay.currency)}
              </b>
            </div>
          ) : null}
        </div>
        {isSuper && pay?.payrollFrozen ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="alertTriangle" size={14} />
            Bu dönemin bordrosu ({pay.payrollFrozen}) hesaplanmış — ödenecek gerçek tutar bordroda DONMUŞTUR.
            Buradaki rakam güncel puantajdan canlı hesaplanır; mesai girdikten sonra bordroyu yeniden hesaplayın.
          </div>
        ) : null}
        {isSuper && pay && !pay.payOvertime ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="alertTriangle" size={14} />
            Bordro politikasında “fazla mesaiyi ücrete ekle” KAPALI — saat kaydedilir ama ücret ödenmez.
          </div>
        ) : null}

        <div className="bulk-cols">
          {/* ── Sol: personel seçimi ── */}
          <section className="d-card bulk-pick">
            <div className="d-card-head">
              <div className="left"><span className="t">Personel</span><span className="s">{filtered.length} kişi</span></div>
              <span className="bulk-head-actions">
                <button type="button" className="bulk-link" onClick={selectAllShown}>Görünenleri seç</button>
                {picked.size > 0 ? <button type="button" className="bulk-link" onClick={() => setPicked(new Map())}>Temizle</button> : null}
              </span>
            </div>
            <div className="d-card-body">
              <input
                className="form-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ad, kod ya da pozisyon ara…"
                aria-label="Personel ara"
              />
            </div>
            <div className="d-card-body flush bulk-picklist">
              {monthQ.isLoading ? (
                <div className="mt-empty">Yükleniyor…</div>
              ) : monthQ.isError ? (
                <div className="mt-empty">{monthQ.error.message}</div>
              ) : filtered.length === 0 ? (
                <div className="mt-empty">Eşleşen personel yok.</div>
              ) : (
                <ul className="bulk-ul">
                  {filtered.map((p) => {
                    const on = picked.has(p.employeeId);
                    const rt = rateOf(p.employeeId);
                    // Saat ücreti = oran × katsayı. Katsayı YALNIZ oranı tanımlı olmayan
                    // personelde 1'den farklıdır (ücret maaştan türetilir) — ikisinin çarpımı
                    // her iki yolda da "bir saatlik mesainin fiyatı"dır.
                    const perHour = rt ? rt.hourlyRate * rt.multiplier : null;
                    return (
                      <li key={p.employeeId}>
                        <label className={`bulk-li${on ? " on" : ""}`}>
                          <input type="checkbox" checked={on} onChange={() => toggle(p.employeeId)} />
                          <span className="bulk-li-main">
                            <span className="bulk-li-name">{p.name}</span>
                            <span className="bulk-li-sub">
                              {p.positionTitle ?? "pozisyon yok"}
                              {isSuper && perHour !== null && perHour > 0 ? (
                                <>
                                  {" · "}
                                  <span
                                    className="bulk-li-rate"
                                    title={`Saat ücreti — kaynak: ${rt!.rateSource ? RATE_SOURCE_LABEL[rt!.rateSource] ?? rt!.rateSource : "maaştan türetildi"}`}
                                  >
                                    {fmtRateTL(perHour, rt!.currency)}/sa
                                  </span>
                                </>
                              ) : null}
                            </span>
                          </span>
                          <span className="bulk-li-fig">
                            <span className={p.overtimeHours > 0 ? "bulk-li-h" : "bulk-li-dash"} title="Bu ay girilmiş toplam mesai">
                              {p.overtimeHours > 0 ? `${fmtH(p.overtimeHours)} sa` : "—"}
                            </span>
                            {isSuper ? (
                              <span className={p.overtimeHours > 0 ? "bulk-li-pay" : "bulk-li-dash"} title="Bu ayki toplam mesai alacağı">
                                {rt && p.overtimeHours > 0 ? fmtTL(rt.overtimePay, rt.currency) : "—"}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          {/* ── Sağ: seçilenler + tarih/saat ── */}
          <section className="d-card bulk-rows">
            <div className="d-card-head">
              <div className="left"><span className="t">Seçilenler</span><span className="s">{rows.length} satır</span></div>
            </div>
            <div className="d-card-body bulk-apply">
              <div className="form-field">
                <label className="lbl" htmlFor="bo-ad">Tarih</label>
                <input id="bo-ad" className="form-input" type="date" min={per.min} max={per.max} value={applyDate} onChange={(e) => setApplyDate(e.target.value)} />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="bo-ah">Mesai (saat)</label>
                <input id="bo-ah" className="form-input mono" inputMode="decimal" value={applyHours} onChange={(e) => setApplyHours(e.target.value)} placeholder="0" />
              </div>
              <button type="button" className="btn-md secondary" disabled={rows.length === 0 || (!applyDate && !applyHours)} onClick={spread}>
                <Icon name="copy" size={14} />Hepsine uygula
              </button>
            </div>
            <div className="d-card-body flush bulk-rowlist">
              {rows.length === 0 ? (
                <div className="mt-empty">Soldan personel seçin; seçtikleriniz burada satır olur.</div>
              ) : (
                <table className="dt2">
                  <thead>
                    <tr>
                      <th>Personel</th>
                      <th style={{ width: 148 }}>Tarih</th>
                      <th className="num" style={{ width: 96 }}>Mesai (sa)</th>
                      {isSuper ? <th className="num" style={{ width: 118 }}>Saat Ücreti</th> : null}
                      {isSuper ? <th className="num" style={{ width: 118 }}>Tutar</th> : null}
                      <th className="num" style={{ width: 150 }} title="Bu kayıttan sonra personelin o ayki TOPLAM mesai hakedişi">
                        {isSuper ? "Aylık Hakediş" : "Ay Toplamı"}
                      </th>
                      <th style={{ width: 40 }} aria-label="Kaldır" />
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((r) => {
                      const p = nameOf.get(r.id);
                      const rt = rateOf(r.id);
                      return (
                        <tr key={r.id}>
                          <td>
                            {p?.name ?? r.id}
                            <span className="bulk-li-sub"> {p?.positionTitle ?? ""}</span>
                          </td>
                          <td>
                            <input
                              className={`form-input${r.dateOk ? "" : " err"}`}
                              type="date"
                              min={per.min}
                              max={per.max}
                              value={r.date}
                              onChange={(e) => setRow(r.id, { date: e.target.value })}
                              aria-label={`${p?.name ?? ""} tarih`}
                              aria-invalid={!r.dateOk}
                              title={r.dateOk ? undefined : `Tarih ${per.label} dönemi içinde olmalı`}
                            />
                          </td>
                          <td>
                            <input
                              className={`form-input mono${r.hoursOk ? "" : " err"}`}
                              inputMode="decimal"
                              value={r.hours}
                              onChange={(e) => setRow(r.id, { hours: e.target.value })}
                              placeholder="0"
                              aria-label={`${p?.name ?? ""} mesai saati`}
                              aria-invalid={!r.hoursOk}
                              title={r.hoursOk ? undefined : "0 ile 16 arası bir saat girin (ör. 2 ya da 1,5)"}
                            />
                          </td>
                          {isSuper ? (
                            <td className="cell-num">
                              {rt && rt.hourlyRate > 0 ? (
                                <span title={`Kaynak: ${rt.rateSource ? RATE_SOURCE_LABEL[rt.rateSource] ?? rt.rateSource : "maaştan türetildi"}${rt.multiplier !== 1 ? ` · katsayı ${rt.multiplier}` : ""}`}>
                                  {fmtRateTL(rt.hourlyRate * rt.multiplier, rt.currency)}
                                </span>
                              ) : (
                                <span className="bulk-dash" title="Saat ücreti çözülemedi: pozisyona ya da kişiye mesai oranı girin; oran yoksa ücret maaştan türetilir ve maaş kaydı da gerekir">—</span>
                              )}
                            </td>
                          ) : null}
                          {isSuper ? (
                            <td className="cell-num">
                              {r.valid && rowPay(r.id, r.h) !== null
                                ? fmtTL(rowPay(r.id, r.h)!, rt!.currency)
                                : <span className="bulk-dash">—</span>}
                            </td>
                          ) : null}
                          {(() => {
                            // AYLIK HAKEDİŞ — bu satır kaydedildikten SONRAKİ ay toplamı.
                            const m = monthAfterRow(r.id, r.date, r.valid ? r.h : 0);
                            return (
                              <td className="cell-num bulk-month">
                                {isSuper ? (
                                  m.amount === null
                                    ? <span className="bulk-dash" title="Saat ücreti çözülemedi — pozisyona ya da kişiye oran girin (ya da personelin maaş kaydını tanımlayın)">—</span>
                                    : <b>{fmtTL(m.amount, m.currency)}</b>
                                ) : null}
                                <span className="bulk-sub">{fmtH(m.hours)} sa</span>
                              </td>
                            );
                          })()}
                          <td>
                            <button
                              type="button"
                              className="iact danger"
                              title="Satırı kaldır"
                              aria-label={`${p?.name ?? ""} satırını kaldır`}
                              onClick={() => toggle(r.id)}
                            >
                              <Icon name="x" size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>

        <p className="bulk-note">
          Aynı personel-gün için saat <b>üzerine yazılır</b>, toplanmaz — listeyi iki kez kaydetmek çift mesai oluşturmaz.
          Girilen mesai “Puantaj Hesapla” ile <b>silinmez</b>: yeniden hesap o günün durumunu tazeler ama mesai saatine dokunmaz.
        </p>
      </PageFormShell>
      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );
}
